import React from 'react';
import { toast } from 'react-toastify';
import { AUTH_SERVICE_URL } from '../../helper/constant';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            errorInfo: null,
            isReloading: false
        };
        
        // Bind methods
        this.handleReload = this.handleReload.bind(this);
        this.isDeploymentError = this.isDeploymentError.bind(this);
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        console.error('ErrorBoundary caught an error:', error, errorInfo);
        
        this.setState({
            errorInfo: errorInfo
        });

        try {
            const message = error && (error.message || error.toString && error.toString()) || 'Unknown error';
            const stack = error && error.stack ? String(error.stack) : '';
            const url = window.location && window.location.href ? window.location.href : '';
            const userAgent = navigator && navigator.userAgent ? navigator.userAgent : '';
            let portalId = 0, clientId = 0, username = '';
            try {
                const params = new URLSearchParams(window.location.search || '');
                portalId = parseInt(params.get('pId') || params.get('portalId') || '0', 10) || 0;
                clientId = parseInt(params.get('cId') || params.get('clientId') || '0', 10) || 0;
                username = params.get('username') || '';
            } catch (e) {
                // ignore
            }
            let appVersion = '';
            try {
                appVersion = localStorage.getItem('app-version') || '';
            } catch (e) {
                // ignore
            }

            const payload = {
                message: String(message),
                stack,
                url,
                userAgent,
                portalId,
                clientId,
                username,
                appVersion,
                extra: {
                    componentStack: errorInfo && errorInfo.componentStack ? String(errorInfo.componentStack) : '',
                    source: 'ErrorBoundary',
                },
                ts: new Date().toISOString(),
            };

            const endpoint = `${AUTH_SERVICE_URL}/portal/client-error`;
            if (navigator && typeof navigator.sendBeacon === 'function') {
                const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
                navigator.sendBeacon(endpoint, blob);
            } else {
                fetch(endpoint, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                    keepalive: true,
                }).catch(() => { /* ignore */ });
            }
        } catch (e) {
            // ignore
        }

        // Check if this is a deployment-related error
        if (this.isDeploymentError(error)) {
            console.log('Deployment-related error detected, auto-reloading...');
            this.handleAutoReload();
        }
    }

    isDeploymentError(error) {
        if (!error) return false;
        
        const errorMessage = error.toString().toLowerCase();
        const errorStack = error.stack ? error.stack.toLowerCase() : '';
        
        // Common deployment-related error patterns
        const deploymentErrorPatterns = [
            'removechild',
            'node to be removed is not a child',
            'cannot read property',
            'cannot read properties of null',
            'loading chunk',
            'loading css chunk',
            'unexpected token',
            'syntaxerror',
            'script error',
            'network error',
            'chunkloaderror',
            'failed to fetch',
            // Additional DOM manipulation error patterns
            'notfounderror',
            'failed to execute',
            'appendchild',
            'insertbefore',
            'dom manipulation',
            'react-dom',
            'component unmount',
            'timer cleanup',
            'interval cleanup'
        ];

        // Check if error message or stack contains deployment-related patterns
        return deploymentErrorPatterns.some(pattern => 
            errorMessage.includes(pattern) || errorStack.includes(pattern)
        );
    }

    handleAutoReload() {
        if (this.state.isReloading) return;

        this.setState({ isReloading: true });

        // Show a brief notification before reloading
        toast.info('Application updated. Reloading...', {
            autoClose: 1500,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: false,
        });

        // Reload after a short delay to allow the toast to show
        setTimeout(() => {
            // Clear any cached data
            if ('caches' in window) {
                caches.keys().then(names => {
                    names.forEach(name => {
                        caches.delete(name);
                    });
                });
            }
            
            // Force reload with cache bypass
            window.location.reload(true);
        }, 2000);
    }

    handleReload() {
        this.handleAutoReload();
    }

    render() {
        if (this.state.hasError) {
            // If it's a deployment error and we're already reloading, show minimal UI
            if (this.state.isReloading) {
                return (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '100vh',
                        backgroundColor: '#f8f9fa',
                        fontFamily: 'Arial, sans-serif'
                    }}>
                        <div style={{
                            textAlign: 'center',
                            padding: '2rem',
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                            maxWidth: '400px'
                        }}>
                            <div style={{
                                width: '40px',
                                height: '40px',
                                border: '4px solid #f3f3f3',
                                borderTop: '4px solid #007bff',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite',
                                margin: '0 auto 1rem'
                            }}></div>
                            <h3 style={{ color: '#333', marginBottom: '0.5rem' }}>Updating Application</h3>
                            <p style={{ color: '#666', margin: 0 }}>Please wait while we reload the latest version...</p>
                        </div>
                        <style>
                            {`
                                @keyframes spin {
                                    0% { transform: rotate(0deg); }
                                    100% { transform: rotate(360deg); }
                                }
                            `}
                        </style>
                    </div>
                );
            }

            // For other errors, show error UI with reload option
            return (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    backgroundColor: '#f8f9fa',
                    fontFamily: 'Arial, sans-serif',
                    padding: '1rem'
                }}>
                    <div style={{
                        textAlign: 'center',
                        padding: '2rem',
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                        maxWidth: '500px'
                    }}>
                        <div style={{
                            fontSize: '3rem',
                            color: '#dc3545',
                            marginBottom: '1rem'
                        }}>⚠️</div>
                        <h2 style={{ color: '#333', marginBottom: '1rem' }}>Something went wrong</h2>
                        <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                            An unexpected error occurred. This might be due to a recent application update.
                        </p>
                        <button
                            onClick={this.handleReload}
                            style={{
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                padding: '0.75rem 1.5rem',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '1rem',
                                marginRight: '0.5rem'
                            }}
                            onMouseOver={(e) => e.target.style.backgroundColor = '#0056b3'}
                            onMouseOut={(e) => e.target.style.backgroundColor = '#007bff'}
                        >
                            Reload Application
                        </button>
                        
                        {/* Show error details in development */}
                        {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
                            <details style={{ 
                                marginTop: '1rem', 
                                textAlign: 'left',
                                backgroundColor: '#f8f9fa',
                                padding: '1rem',
                                borderRadius: '4px',
                                fontSize: '0.875rem'
                            }}>
                                <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                                    Error Details (Development Mode)
                                </summary>
                                <pre style={{ 
                                    whiteSpace: 'pre-wrap', 
                                    wordBreak: 'break-word',
                                    marginTop: '0.5rem',
                                    color: '#721c24'
                                }}>
                                    {this.state.errorInfo.componentStack}
                                </pre>
                            </details>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
